import { createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from 'app/store';
import { BulkValidSystemsResponseType } from 'data/types';
import axios from 'utils/api';
import {
  BACKGROUND_PAGE_SIZE,
  getValidSystemsRequest
} from 'utils/valid-systems-request-registry';
import {
  appendSystemCombinationsFull,
  setCombinationLoadingMore
} from './systems-slice';

// Loads the next page of scored combinations for a configuration by
// re-sending the exact request body the valid-systems watcher last sent
// (from the request registry) with an incremented page. Identical inputs hit
// the server's scored-combinations cache, so appended pages never overlap or
// skip. Page>0 cache hits return perCondenser as [] (cache misses return it
// populated); either way this thunk never reads perCondenser, scenarios, or
// scenarioWinners — the page-0 data stays authoritative.
export const loadMoreSystemCombinations = createAsyncThunk(
  'systems/loadMoreSystemCombinations',
  async (configId: string, { dispatch, getState }) => {
    const state = getState() as RootState;
    const pagination = state.systems.combinationPaginationByConfig[configId];
    if (!pagination || pagination.loadingMore) return;
    const loaded = state.systems.systemCombinationsFull[configId]?.length ?? 0;
    if (loaded >= pagination.total) return;

    const entry = getValidSystemsRequest(configId);
    if (!entry || entry.hash !== pagination.requestBodyHash) return;

    dispatch(setCombinationLoadingMore({ configId, loadingMore: true }));
    try {
      const response = (
        await axios.post('valid-systems', {
          ...entry.body,
          page: pagination.loadedPage + 1,
          pageSize: BACKGROUND_PAGE_SIZE
        })
      ).data as BulkValidSystemsResponseType;

      // The reducer discards the append if the watcher refetched page 0
      // with a different request body while this was in flight.
      dispatch(
        appendSystemCombinationsFull({
          configId,
          combinations: response.combinations,
          page: response.pagination.page,
          total: response.pagination.total,
          requestBodyHash: entry.hash
        })
      );
    } catch (error) {
      console.error(
        `[systems-thunks] load-more combinations failed for config ${configId}:`,
        error
      );
    } finally {
      dispatch(setCombinationLoadingMore({ configId, loadingMore: false }));
    }
  }
);
